import { MailOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Space, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { contactsApi } from '../../services/contactsApi';

const SendCVModal = ({ data, isOpen, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);

  // Utilities
  const stripHtml = (html) => {
    if (!html) return '';
    let text = String(html)
      .replace(/<br\s*\/?>(\s*)/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<[^>]+>/g, '');
    // Collapse excessive blank lines
    text = text.replace(/\n{3,}/g, '\n\n').trim();
    return text;
  };

  const extractJsonFromText = (text) => {
    if (!text) return null;
    try {
      // Try fenced block first
      const fenceMatch = text.match(/```[a-zA-Z]*\n([\s\S]*?)```/);
      const payload = fenceMatch ? fenceMatch[1] : text;
      const jsonMatch = payload.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (_) {
      // ignore
    }
    return null;
  };

  const resolveCandidateId = () =>
    data?.contact?.id ||
    data?.id ||
    data?._id ||
    data?.documentId ||
    data?.contactId ||
    '';

  const resolveLatestJob = () =>
    data?.contact?.lastEvaluatedJob ||
    data?.lastEvaluatedJob ||
    data?.job ||
    data?.jobData ||
    null;

  const defaultEmail = useMemo(() => {
    return localStorage.getItem('lastClientEmail') || '';
  }, []);

  useEffect(() => {
    if (isOpen && data) {
      const jobTitle = data?.contact?.lastEvaluatedJob?.title || data?.title || 'Position';
      form.setFieldsValue({
        clientEmail: defaultEmail,
        subject: `Candidate Profile Submission for the Role: ${jobTitle}`,
        message: `Please find attached the CV for ${data?.contact?.contactName || data?.contactName}.\n\nSummary:\n- Title: ${data?.title || 'N/A'}\n- Email: ${data?.email || 'N/A'}\n- Phone: ${data?.phone || 'N/A'}\n\nNotes: `,
      });
    }
  }, [isOpen, data, form, defaultEmail]);

  const onFinish = async (values) => {
    if (!data) return;
    try {
      setLoading(true);
      localStorage.setItem('lastClientEmail', values.clientEmail || '');

      const payload = {
        contactId: data?.contact?.id || data?.id || data?._id || data?.documentId,
        contactName: data?.contact?.contactName || data?.contactName,
        cvText: data?.cvText || data?.text || '',
        clientEmail: values.clientEmail,
        subject: values.subject,
        message: values.message,
        meta: {
          title: data?.title,
          email: data?.email,
          phone: data?.phone,
          lastEvaluatedJob: data?.contact?.lastEvaluatedJob || data?.lastEvaluatedJob || null,
          evaluationSummary: data?.evaluationSummary || null,
        },
        // Optional attachment from branded preview
        attachmentPdfB64: data?.attachmentPdfB64 || null,
        attachmentName: data?.attachmentName || null,
      };

      const resp = await contactsApi.sendCV(payload);
      if (resp?.success) {
        const latestJob = resolveLatestJob();
        const latestJobId =
          latestJob?.id ||
          latestJob?.job_id ||
          latestJob?._id ||
          latestJob?.documentId ||
          data?.jobId ||
          data?.job_id ||
          data?.contact?.lastEvaluatedJob?.id ||
          data?.lastEvaluatedJob?.id ||
          '';

        if (latestJobId && resolveCandidateId()) {
          try {
            await contactsApi.upsertPlacementJourney({
              candidate_id: resolveCandidateId(),
              job_id: latestJobId,
              stage: 'submitted',
              status: 'active',
              source: 'send_cv_modal',
              actor: 'alice_agent',
              submission: {
                submitted_at: new Date().toISOString(),
                submitted_by: 'alice_agent',
                submission_method: 'email',
                cv_sent: true,
                jd_sent: Boolean(data?.jobPdfB64 || data?.attachmentJdPdfB64),
                evaluation_report_sent: Boolean(data?.evaluationSummary)
              },
              candidate_snapshot: {
                id: resolveCandidateId(),
                name: data?.contact?.contactName || data?.contactName || data?.name || '',
                title: data?.title || data?.contact?.title || '',
                email: data?.email || data?.contact?.email || '',
                phone: data?.phone || data?.contact?.phone || ''
              },
              job_snapshot: {
                id: latestJobId,
                title: latestJob?.title || latestJob?.job_title || '',
                company: latestJob?.company || latestJob?.employer_name || '',
                location: latestJob?.location || ''
              },
              metadata: {
                subject: values.subject,
                email_recipient: values.clientEmail
              }
            });
          } catch (journeyError) {
            console.warn('Placement journey upsert skipped after CV send:', journeyError);
          }
        }
        message.success('CV sent successfully');
        onClose && onClose();
        form.resetFields();
      } else {
        message.error(resp?.error || 'Failed to send CV');
      }
    } catch (err) {
      message.error(err?.message || 'Failed to send CV');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={(data?.contact?.contactName || data?.contactName) ? `Send CV - ${(data?.contact?.contactName || data?.contactName)}` : 'Send CV'}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
    >
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Form.Item
          label="Recipient Email"
          name="clientEmail"
          rules={[{ required: true, message: 'Please enter recipient email' }, { type: 'email', message: 'Invalid email' }]}
        >
          <Input placeholder="hiring.manager@company.com" prefix={<MailOutlined />} />
        </Form.Item>

        <Form.Item label="Subject" name="subject" rules={[{ required: true, message: 'Please enter subject' }]}>
          <Input placeholder="CV for [Position]" />
        </Form.Item>

        <Form.Item
          label={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span>Message</span>
              <span style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: 'normal' }}>
                Supports markdown: **bold**, *italic*, - bullets, [links](url)
              </span>
            </div>
          }
          name="message"
          rules={[{ required: true, message: 'Please enter message' }]}
        >
          <Input.TextArea
            rows={6}
            placeholder="Add any additional context or notes...&#10;&#10;You can use markdown formatting:&#10;**bold text** for bold&#10;*italic text* for italic&#10;- Bullet point&#10;1. Numbered list&#10;[Link text](https://example.com) for links"
          />
        </Form.Item>

        <Space>
          <Button onClick={async () => {
            if (!data) return;
            try {
              setGenLoading(true);
              const payload = {
                contactId: data?.contact?.id || data?.id || data?._id || data?.documentId,
                jobId: data?.lastEvaluatedJob?.id || data?.contact?.lastEvaluatedJob?.id,
                jobTitle: data?.lastEvaluatedJob?.title || data?.contact?.lastEvaluatedJob?.title,
                jobCompany: data?.lastEvaluatedJob?.company || data?.contact?.lastEvaluatedJob?.company,
                evaluationSummary: data?.evaluationSummary || null,
                candidate: {
                  name: data?.contact?.contactName || data?.contactName,
                  title: data?.title,
                  skills: data?.skills || [],
                },
              };
              const resp = await contactsApi.generateCVCover(payload);
              if (resp?.success) {
                let subject = resp.subject;
                let coverMessage = resp.message;
                // If backend fell back to raw content with JSON inside, extract it
                if ((!subject || !coverMessage) && typeof coverMessage === 'string') {
                  const parsed = extractJsonFromText(coverMessage);
                  if (parsed) {
                    subject = parsed.subject || subject;
                    coverMessage = parsed.message || coverMessage;
                  }
                }
                // Sanitize message for the textarea (no HTML tags)
                const cleaned = stripHtml(coverMessage);
                form.setFieldsValue({
                  subject: subject || form.getFieldValue('subject'),
                  message: cleaned || form.getFieldValue('message')
                });
                message.success('Cover email generated');
              } else {
                message.error(resp?.error || 'Failed to generate');
              }
            } catch (e) {
              message.error(e?.message || 'Failed to generate');
            } finally {
              setGenLoading(false);
            }
          }} loading={genLoading}>Generate</Button>
          <Button type="primary" htmlType="submit" loading={loading}>Send CV</Button>
          <Button onClick={onClose}>Cancel</Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default SendCVModal;
